import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { toasterNotification } from './toasterNotification';

const findErrorMessage = (obj: any, depth = 0): string | null => {
	if (depth > 5) return null;

	if (!obj || typeof obj !== 'object') return null;

	const messageKeys = ['message', 'error', 'msg', 'errorMessage', 'detail'];
	const foundKey = messageKeys.find((key) => {
		if (obj[key]) {
			const value = obj[key];
			if (typeof value === 'string') {
				return true;
			}
			if (typeof value === 'object') {
				const nested = findErrorMessage(value, depth + 1);
				return nested !== null;
			}
		}
		return false;
	});

	if (foundKey) {
		const value = obj[foundKey];
		if (typeof value === 'string') {
			return value;
		}
		if (typeof value === 'object') {
			const nested = findErrorMessage(value, depth + 1);
			if (nested) return nested;
		}
	}

	const objKeys = Object.keys(obj);
	const foundString = objKeys.find((key) => {
		const value = obj[key];
		if (typeof value === 'string' && value.length > 0) {
			return true;
		}
		if (typeof value === 'object' && value !== null) {
			const nested = findErrorMessage(value, depth + 1);
			return nested !== null;
		}
		return false;
	});

	if (foundString) {
		const value = obj[foundString];
		if (typeof value === 'string' && value.length > 0) {
			return value;
		}
		if (typeof value === 'object' && value !== null) {
			const nested = findErrorMessage(value, depth + 1);
			if (nested) return nested;
		}
	}

	return null;
};

export const extractErrorMessage = (error: any): string => {
	const foundMsg = findErrorMessage(error);
	if (foundMsg) {
		const errorMsg = foundMsg.toLowerCase();

		if (
			errorMsg.includes('e11000') ||
			errorMsg.includes('duplicate key') ||
			errorMsg.includes('duplicate')
		) {
			if (errorMsg.includes('email')) {
				return 'A company with this email already exists';
			}
			if (errorMsg.includes('name')) {
				return 'A company with this name already exists';
			}
			return 'This record already exists';
		}

		if (
			errorMsg.includes('bsontypeerror') ||
			errorMsg.includes('objectid') ||
			errorMsg.includes('must be a string of 12 bytes') ||
			errorMsg.includes('must be a string of 24 hex characters') ||
			errorMsg.includes('invalid id')
		) {
			return 'Invalid ID format. Please provide a valid ID.';
		}

		return foundMsg;
	}

	if (typeof error === 'string') {
		const errorMsg = error.toLowerCase();
		if (errorMsg.includes('e11000') || errorMsg.includes('duplicate')) {
			if (errorMsg.includes('email')) {
				return 'A company with this email already exists';
			}
			if (errorMsg.includes('name')) {
				return 'A company with this name already exists';
			}
			return 'This record already exists';
		}
		if (
			errorMsg.includes('bsontypeerror') ||
			errorMsg.includes('objectid') ||
			errorMsg.includes('must be a string of 12 bytes') ||
			errorMsg.includes('must be a string of 24 hex characters')
		) {
			return 'Invalid ID format. Please provide a valid ID.';
		}
		return error;
	}

	if (error?.message) {
		const errorMsg = error.message.toLowerCase();
		if (
			errorMsg.includes('e11000') ||
			errorMsg.includes('duplicate key') ||
			errorMsg.includes('duplicate')
		) {
			if (errorMsg.includes('email')) {
				return 'A company with this email already exists';
			}
			if (errorMsg.includes('name')) {
				return 'A company with this name already exists';
			}
			return 'This record already exists';
		}
		if (
			errorMsg.includes('bsontypeerror') ||
			errorMsg.includes('objectid') ||
			errorMsg.includes('must be a string of 12 bytes') ||
			errorMsg.includes('must be a string of 24 hex characters')
		) {
			return 'Invalid ID format. Please provide a valid ID.';
		}
		return error.message;
	}

	if (error?.response?.data) {
		const errorData = error.response.data;

		if (typeof errorData === 'string') {
			const errorMsg = errorData.toLowerCase();
			if (errorMsg.includes('e11000') || errorMsg.includes('duplicate')) {
				if (errorMsg.includes('email')) {
					return 'A company with this email already exists';
				}
				if (errorMsg.includes('name')) {
					return 'A company with this name already exists';
				}
				return 'This record already exists';
			}
			if (
				errorMsg.includes('bsontypeerror') ||
				errorMsg.includes('objectid') ||
				errorMsg.includes('must be a string of 12 bytes') ||
				errorMsg.includes('must be a string of 24 hex characters')
			) {
				return 'Invalid ID format. Please provide a valid ID.';
			}
			return errorData;
		}

		if (errorData.message) {
			const errorMsg = errorData.message.toLowerCase();
			if (
				errorMsg.includes('e11000') ||
				errorMsg.includes('duplicate key') ||
				errorMsg.includes('duplicate')
			) {
				if (errorMsg.includes('email')) {
					return 'A company with this email already exists';
				}
				if (errorMsg.includes('name')) {
					return 'A company with this name already exists';
				}
				return 'This record already exists';
			}
			if (
				errorMsg.includes('bsontypeerror') ||
				errorMsg.includes('objectid') ||
				errorMsg.includes('must be a string of 12 bytes') ||
				errorMsg.includes('must be a string of 24 hex characters')
			) {
				return 'Invalid ID format. Please provide a valid ID.';
			}
			return errorData.message;
		}

		if (Array.isArray(errorData.message)) {
			const validationErrors = errorData.message.filter(
				(msg: any) => typeof msg === 'string',
			);
			if (validationErrors.length > 0) {
				return validationErrors.join(', ');
			}
		}

		if (errorData.error) {
			const errorMsg = errorData.error.toLowerCase();
			if (
				errorMsg.includes('e11000') ||
				errorMsg.includes('duplicate key') ||
				errorMsg.includes('duplicate')
			) {
				if (errorMsg.includes('email')) {
					return 'A company with this email already exists';
				}
				if (errorMsg.includes('name')) {
					return 'A company with this name already exists';
				}
				return 'This record already exists';
			}
			if (
				errorMsg.includes('bsontypeerror') ||
				errorMsg.includes('objectid') ||
				errorMsg.includes('must be a string of 12 bytes') ||
				errorMsg.includes('must be a string of 24 hex characters')
			) {
				return 'Invalid ID format. Please provide a valid ID.';
			}
			return errorData.error;
		}

		const nestedMsg = findErrorMessage(errorData);
		if (nestedMsg) {
			return nestedMsg;
		}
	}

	if (error?.statusCode === 409 || error?.response?.status === 409) {
		const conflictMsg = findErrorMessage(error?.response?.data || error);
		if (conflictMsg) {
			const errorMsg = conflictMsg.toLowerCase();
			if (errorMsg.includes('email')) {
				return 'A company with this email already exists';
			}
			if (errorMsg.includes('name')) {
				return 'A company with this name already exists';
			}
			return conflictMsg;
		}
		return 'A company with this information already exists';
	}

	if (error?.statusCode === 400 || error?.response?.status === 400) {
		const badRequestMsg = findErrorMessage(error?.response?.data || error);
		if (badRequestMsg) {
			return badRequestMsg;
		}
		return 'Invalid request. Please check your input.';
	}

	if (error?.statusCode === 404 || error?.response?.status === 404) {
		return 'Resource not found.';
	}

	if (error?.statusCode === 500 || error?.response?.status === 500) {
		const serverErrorMsg = findErrorMessage(error?.response?.data || error);
		if (serverErrorMsg && serverErrorMsg !== 'An error occurred') {
			return serverErrorMsg;
		}
		return 'Server error. Please try again later.';
	}

	try {
		const errorString = JSON.stringify(error).toLowerCase();
		if (errorString.includes('e11000') || errorString.includes('duplicate')) {
			if (errorString.includes('email')) {
				return 'A company with this email already exists';
			}
			if (errorString.includes('name')) {
				return 'A company with this name already exists';
			}
			return 'This record already exists';
		}
		if (
			errorString.includes('bsontypeerror') ||
			errorString.includes('objectid') ||
			errorString.includes('must be a string of 12 bytes') ||
			errorString.includes('must be a string of 24 hex characters')
		) {
			return 'Invalid ID format. Please provide a valid ID.';
		}
	} catch (e) {
		// JSON.stringify failed (circular reference), continue
	}

	return 'An error occurred. Please try again.';
};

export const commonErrorHandler = (err: any) => {
	let msg = err;
	if (Array.isArray(msg)) [msg] = msg;
	if (msg.message) msg = msg.message;
	if (msg.error) msg = msg.error;
	if (msg.data) msg = msg.data;
	if (msg.msg) msg = msg.msg;
	if (Array.isArray(msg)) [msg] = msg;
	if (typeof msg !== 'string') {
		msg = 'Ocurrio un error';
	}
	toasterNotification({
		msg,
		toasterType: toasterOptions.error,
		style: 'light',
	});
};
